/**
 * VideoPreviewOverlay - In-app video preview using Chromium's native media player.
 */

import { useEffect, useMemo, useState } from 'react'
import { FilePlay } from 'lucide-react'
import { PreviewOverlay } from './PreviewOverlay'
import { CopyButton } from './CopyButton'
import { ItemNavigator } from './ItemNavigator'
import { useTranslation } from '@okclawpro/i18n/react'

interface PreviewItem {
  src: string
  label?: string
}

export interface VideoPreviewOverlayProps {
  isOpen: boolean
  onClose: () => void
  /** Absolute file path for the video (single item / backward compat) */
  filePath: string
  /** Multiple items for arrow navigation */
  items?: PreviewItem[]
  /** Initial active item index (defaults to 0) */
  initialIndex?: number
  theme?: 'light' | 'dark'
  embedded?: boolean
}

export function toVideoMediaUrl(filePath: string): string {
  const trimmed = filePath.trim()
  if (!trimmed) return ''
  return `media://video/${encodeURIComponent(trimmed)}`
}

export function VideoPreviewOverlay({
  isOpen,
  onClose,
  filePath,
  items,
  initialIndex = 0,
  theme = 'light',
  embedded = false,
}: VideoPreviewOverlayProps) {
  const { t } = useTranslation()
  const resolvedItems = useMemo<PreviewItem[]>(() => {
    if (items && items.length > 0) return items
    return [{ src: filePath }]
  }, [items, filePath])

  const [activeIdx, setActiveIdx] = useState(initialIndex)
  const [loadFailed, setLoadFailed] = useState(false)

  const activeItem = resolvedItems[activeIdx]
  const activeVideoUrl = activeItem?.src ? toVideoMediaUrl(activeItem.src) : ''

  useEffect(() => {
    if (isOpen) {
      const bounded = Math.max(0, Math.min(initialIndex, resolvedItems.length - 1))
      setActiveIdx(bounded)
    }
  }, [isOpen, initialIndex, resolvedItems.length])

  useEffect(() => {
    setLoadFailed(false)
  }, [activeVideoUrl])

  const headerActions = (
    <div className="flex items-center gap-2">
      <ItemNavigator items={resolvedItems} activeIndex={activeIdx} onSelect={setActiveIdx} size="md" />
      <CopyButton content={activeItem?.src || filePath} title={t('overlay.copyPath')} className="bg-background shadow-minimal" />
    </div>
  )

  return (
    <PreviewOverlay
      isOpen={isOpen}
      onClose={onClose}
      theme={theme}
      typeBadge={{
        icon: FilePlay,
        label: 'Video',
        variant: 'blue',
      }}
      filePath={activeItem?.src || filePath}
      error={loadFailed ? { label: t('overlay.loadFailed'), message: t('overlay.videoLoadFailed') } : undefined}
      headerActions={headerActions}
      embedded={embedded}
    >
      <div className="min-h-full flex items-center justify-center p-4">
        {activeVideoUrl && (
          <video
            key={activeVideoUrl}
            src={activeVideoUrl}
            controls
            playsInline
            preload="metadata"
            className="max-h-full max-w-full rounded-sm bg-black shadow-minimal"
            onError={() => setLoadFailed(true)}
          />
        )}
      </div>
    </PreviewOverlay>
  )
}
